// =============================================================================
// FILE: components/automation/FloatingAutomationTile.tsx
// PURPOSE: R2 Floating Automation Tile — always-accessible quick launcher for
//   the Automation Studio. Connects to ConfidenceModal before execution.
//   Quick actions: Run Workflow, Create Workflow, My Tasks, Automation Library.
//   RIGHT-CLICK to move — snap to corner / free drag / reset via R2 Float Control.
// =============================================================================

import { useState, type JSX } from 'react';
import { Zap, Play, Plus, Library, ChevronDown, Sparkles } from 'lucide-react';
import type { Route } from '../../App.js';
import { FloatingWindowFrame } from '../windowing/FloatingWindowFrame.js';

interface FloatingAutomationTileProps {
  onNavigate: (route: Route) => void;
}

const ACCENT = '#a78bfa';

export function FloatingAutomationTile({ onNavigate }: FloatingAutomationTileProps): JSX.Element {
  const [collapsed, setCollapsed] = useState(true);

  const actions = [
    { icon: <Sparkles size={13} />, label: 'AI Create',       action: () => { setCollapsed(true); onNavigate('automation-studio'); } },
    { icon: <Play size={13} />,     label: 'Run Workflow',    action: () => { setCollapsed(true); onNavigate('automation-studio'); } },
    { icon: <Plus size={13} />,     label: 'New Workflow',    action: () => { setCollapsed(true); onNavigate('automation-studio'); } },
    { icon: <Library size={13} />,  label: 'Library',         action: () => { setCollapsed(true); onNavigate('automation-studio'); } },
  ];

  return (
    <FloatingWindowFrame
      id="automation-launcher"
      title="Automation Launcher"
      group="Tools"
      defaultRect={{ x: Math.max(16, window.innerWidth - 280), y: Math.max(92, window.innerHeight - 360), width: 220, height: collapsed ? 100 : 310 }}
      defaultOpen
      canMaximize={false}
      keyboardClosable
      important
      accent={ACCENT}
      contentStyle={{ overflow: 'hidden' }}
    >
    <div
      data-testid="floating-automation-tile"
      data-r2-float-tile="true"
      style={{ userSelect: 'none', height: '100%', background: 'rgba(4,8,20,0.97)' }}
    >
      {collapsed ? (
        <button
          data-testid="automation-tile-expand"
          onClick={() => setCollapsed(false)}
          title="R2 Automation Studio — right-click to reposition"
          style={{
            width: '100%', height: '100%', borderRadius: 0, cursor: 'pointer',
            background: 'rgba(5,10,24,0.95)',
            border: `1px solid ${ACCENT}50`,
            boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 12px ${ACCENT}20`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: ACCENT, backdropFilter: 'blur(12px)',
          }}
        >
          <Zap size={16} />
        </button>
      ) : (
        <div style={{
          background: 'rgba(4,8,20,0.97)',
          border: `1px solid ${ACCENT}30`,
          borderRadius: 14,
          boxShadow: `0 16px 48px rgba(0,0,0,0.65), 0 0 24px ${ACCENT}10`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden', minWidth: 188,
        }}>
          {/* Header — right-click tile for R2 Float Control */}
          <div
            style={{
              padding: '10px 12px 8px',
              display: 'flex', alignItems: 'center', gap: 7,
              cursor: 'default',
              borderBottom: '1px solid rgba(30,50,80,0.4)',
              background: 'rgba(8,14,32,0.6)',
            }}
          >
            <div style={{ display: 'flex', gap: 3, opacity: 0.35 }}>
              {[0,1,2].map(i => <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{[0,1].map(j => <div key={j} style={{ width: 3, height: 3, borderRadius: '50%', background: '#94a3b8' }} />)}</div>)}
            </div>
            <Zap size={11} style={{ color: ACCENT, flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: ACCENT, flex: 1, textTransform: 'uppercase' }}>
              Automation
            </span>
            <button onClick={() => setCollapsed(true)} style={{ background: 'none', border: 'none', color: 'rgba(100,140,180,0.4)', cursor: 'pointer', padding: 0 }}>
              <ChevronDown size={13} />
            </button>
          </div>

          {/* Actions */}
          <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {actions.map(({ icon, label, action }) => (
              <button
                key={label}
                onClick={action}
                data-testid={`automation-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
                style={{
                  padding: '8px 10px', borderRadius: 7, cursor: 'pointer', textAlign: 'left',
                  background: 'rgba(8,14,32,0.5)', border: `1px solid ${ACCENT}20`,
                  color: 'rgba(148,172,210,0.75)', fontSize: 11, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'all 0.12s',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = `${ACCENT}12`;
                  (e.currentTarget as HTMLElement).style.borderColor = `${ACCENT}40`;
                  (e.currentTarget as HTMLElement).style.color = '#dce8f8';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(8,14,32,0.5)';
                  (e.currentTarget as HTMLElement).style.borderColor = `${ACCENT}20`;
                  (e.currentTarget as HTMLElement).style.color = 'rgba(148,172,210,0.75)';
                }}
              >
                <span style={{ color: ACCENT, flexShrink: 0 }}>{icon}</span>
                {label}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div style={{
            padding: '7px 12px', borderTop: '1px solid rgba(30,50,80,0.3)',
            fontSize: 9, color: 'rgba(100,140,180,0.3)', fontFamily: 'monospace', textAlign: 'center',
          }}>
            R2 · Automation Studio
          </div>
        </div>
      )}
    </div>
    </FloatingWindowFrame>
  );
}
